import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BillingRhythm } from '../entities/BillingPosition.js';

/** What a group's Abrechnungsposition bills: the price and the rhythm. */
export interface BillingPositionValues {
  priceInCents: Maybe<number>;
  rhythm: Maybe<BillingRhythm>;
}

/** The one value a booking may override on its group's Abrechnungsposition. */
export interface BillingPositionOverrideValues {
  priceInCents: Maybe<number>;
}

export interface BillingGroupLike {
  _id: string;
  billingPosition?: Maybe<BillingPositionValues>;
}

/**
 * The one group of a booking that carries an Abrechnungsposition - the group that bills the
 * booking. A booking in no such group is not billed; a booking in two of them is ambiguous and is
 * not billed either (the export and the template engine report it instead of guessing). A group
 * listed twice on the booking counts once.
 */
export function getBillingGroup<TGroup extends BillingGroupLike>(
  booking: { groupIDs?: Maybe<string[]> },
  groups: TGroup[],
): TGroup | null {
  const carriers = [...new Set(booking.groupIDs ?? [])]
    .map(groupID => groups.find(group => group._id === groupID))
    .filter((group): group is TGroup => !!group?.billingPosition);

  return carriers.length === 1 ? (carriers[0] ?? null) : null;
}

/**
 * What the booking actually pays: the group's Abrechnungsposition with the booking's own price on
 * top when the booking carries an override. Everything else - the rhythm and what the export books
 * against (name, Kürzel, Kostenstelle, Buchungskonto) - is always the group's.
 */
export function getEffectiveBillingPosition<TBillingPosition extends BillingPositionValues>(
  booking: { billingPosition?: Maybe<BillingPositionOverrideValues> },
  group: { billingPosition: TBillingPosition },
): TBillingPosition {
  const override = booking.billingPosition;
  if (!override) return group.billingPosition;

  /**
   * An entity instance keeps its fields in accessors, which a spread does not copy; its `toJSON`
   * hands out the plain data. A raw document has no `toJSON` and spreads as it is.
   */
  const source = group.billingPosition as TBillingPosition & { toJSON?: () => TBillingPosition };
  const base = typeof source.toJSON === 'function' ? source.toJSON() : source;

  return { ...base, priceInCents: override.priceInCents ?? null };
}

/** Whether an override changes anything - one that repeats the group's price is not stored. */
export function overrideDiffersFromGroup(
  override: BillingPositionOverrideValues,
  groupValues: BillingPositionOverrideValues,
): boolean {
  return (override.priceInCents ?? null) !== (groupValues.priceInCents ?? null);
}
